import React, { createContext, useContext, useEffect, useState, useRef, useMemo } from "react";
import { jwtDecode } from "jwt-decode";
import api from "./api";
import { getUserSubscriptions } from "./api";

export const AuthContext = createContext({});

export const AuthProvider = ({ children }) => {
  const [initialized, setInitialized] = useState(false);
  const [isStartupLoggedIn, setIsStartupLoggedIn] = useState(false);
  const [isMatrimonyLoggedIn, setIsMatrimonyLoggedIn] = useState(false);
  const [isInvestorLoggedIn, setIsInvestorLoggedIn] = useState(false);
  const [isMentorLoggedIn, setIsMentorLoggedIn] = useState(false);


  const [investor, setInvestor] = useState(null);
  const [mentor, setMentor] = useState(null);
  const [subscription, setSubscription] = useState(null);
  const [loadingSubscription, setLoadingSubscription] = useState(false);
  const [subscriptionError, setSubscriptionError] = useState(false);

  // logout timers
  const startupTimer = useRef();
  const matrimonyTimer = useRef();
  const investorTimer = useRef();
  const mentorTimer = useRef(); 

  /* ---------------------------------------------------------
     JWT Auto Logout Scheduler
  --------------------------------------------------------- */
  const scheduleLogout = (token, logoutFn, timerRef) => {
    try {
      const { exp } = jwtDecode(token);
      const ms = exp * 1000 - Date.now();
      if (ms <= 0) return logoutFn();
      clearTimeout(timerRef.current);
      timerRef.current = setTimeout(logoutFn, ms);
    } catch {
      logoutFn();
    }
  };

  /* ---------------------------------------------------------
     AUTH HYDRATION ON PAGE REFRESH (DEMO MODE INCLUDED)
  --------------------------------------------------------- */
  useEffect(() => {
    const sToken = localStorage.getItem("Startuptoken");
    const mToken = localStorage.getItem("matrimonyToken");
    const iToken = localStorage.getItem("investorToken");
    const iData = localStorage.getItem("investor");
    const mentorToken = localStorage.getItem("mentorId");
const mentorData = localStorage.getItem("mentor");
    
if (mentorToken && mentorData) {
  setIsMentorLoggedIn(true);
  setMentor(JSON.parse(mentorData));
  scheduleLogout(mentorToken, logoutMentor, mentorTimer);
}

    // DEMO MODE
    if (sToken === "DEMO_TOKEN") {
      setIsStartupLoggedIn(true);
      setSubscription({
        planName: "Demo Plan",
        tenure: 999,
        createdAt: new Date().toISOString()
      });
      setLoadingSubscription(false);
      setSubscriptionError(false);
      setInitialized(true);
      return;
    }

    if (sToken) {
      setIsStartupLoggedIn(true);
      scheduleLogout(sToken, logoutStartup, startupTimer);
    }

    if (mToken) {
      setIsMatrimonyLoggedIn(true);
      scheduleLogout(mToken, logoutMatrimony, matrimonyTimer);
    }

    if (iToken && iData) {
      setIsInvestorLoggedIn(true);
      setInvestor(JSON.parse(iData));
      scheduleLogout(iToken, logoutInvestor, investorTimer);
    }

    setInitialized(true);
  }, []);

  /* ---------------------------------------------------------
     SUBSCRIPTION FETCH (SKIP IF DEMO USER)
  --------------------------------------------------------- */
  useEffect(() => {
    const sToken = localStorage.getItem("Startuptoken");

    // DEMO USER → Skip API
    if (sToken === "DEMO_TOKEN") {
      setSubscription({
        planName: "Demo Plan",
        tenure: 999,
        createdAt: new Date().toISOString()
      });
      setLoadingSubscription(false);
      setSubscriptionError(false);
      return;
    }

    if (!isStartupLoggedIn) {
      setSubscription(null);
      setLoadingSubscription(false);
      setSubscriptionError(false);
      return;
    }

    let mounted = true;
    setLoadingSubscription(true);

    getUserSubscriptions()
      .then(arr => {
        if (!mounted) return;
        setSubscription(arr?.[0] || null);
        setSubscriptionError(false);
      })
      .catch(() => {
        if (mounted) setSubscriptionError(true);
      })
      .finally(() => {
        if (mounted) setLoadingSubscription(false);
      });

    return () => {
      mounted = false;
    };
  }, [isStartupLoggedIn]);

  /* ---------------------------------------------------------
     401 INTERCEPTOR
  --------------------------------------------------------- */
  useEffect(() => {
    const id = api.interceptors.response.use(
      res => res,
      err => {
        if (err.response?.status === 401) {
          if (isStartupLoggedIn) logoutStartup();
          if (isMatrimonyLoggedIn) logoutMatrimony();
          if (isInvestorLoggedIn) logoutInvestor();
           if (isMentorLoggedIn) logoutMentor(); 
        }
        return Promise.reject(err);
      }
    );

    return () => api.interceptors.response.eject(id);
  }, [isStartupLoggedIn, isMatrimonyLoggedIn, isInvestorLoggedIn]);

  /* ---------------------------------------------------------
     LOGIN / LOGOUT HANDLERS
  --------------------------------------------------------- */

  // ⭐ DEMO MODE
  const loginStartup = token => {
    if (token === "DEMO_TOKEN") {
      localStorage.setItem("Startuptoken", "DEMO_TOKEN");
      localStorage.setItem("startupId", "demo_startup");
      localStorage.setItem("fullName", "Demo User");
      localStorage.setItem("email", "demo@example.com");

      setIsStartupLoggedIn(true);

      setSubscription({
        planName: "Demo Plan",
        tenure: 999,
        createdAt: new Date().toISOString()
      });

      setLoadingSubscription(false);
      setSubscriptionError(false);
      return;
    }

    // REAL LOGIN
    localStorage.setItem("Startuptoken", token);
    setIsStartupLoggedIn(true);
    scheduleLogout(token, logoutStartup, startupTimer);

    // disable others
    if (localStorage.getItem("matrimonyToken")) logoutMatrimony();
    if (localStorage.getItem("investorToken")) logoutInvestor();
  };

  const logoutStartup = () => {
      if (localStorage.getItem("Startuptoken") === "DEMO_TOKEN") return;
    clearTimeout(startupTimer.current);
    localStorage.removeItem("Startuptoken");
    localStorage.removeItem("startupId");
    localStorage.removeItem("fullName");
    localStorage.removeItem("email");
    localStorage.removeItem("startupName");
    localStorage.removeItem("userPhone");
    localStorage.removeItem("logoUrl");
    setIsStartupLoggedIn(false);
  };

  const loginMatrimony = token => {
    localStorage.setItem("matrimonyToken", token);
    setIsMatrimonyLoggedIn(true);
    scheduleLogout(token, logoutMatrimony, matrimonyTimer);

    if (localStorage.getItem("Startuptoken")) logoutStartup();
    if (localStorage.getItem("investorToken")) logoutInvestor();
  };

  const logoutMatrimony = () => {
    clearTimeout(matrimonyTimer.current);
    localStorage.removeItem("matrimonyToken");
    localStorage.removeItem("matrimonyUserId");
    localStorage.removeItem("matrimonyUserName");
    localStorage.removeItem("matrimonyUserEmail");
    localStorage.removeItem("UserFounderId");
    localStorage.removeItem("UserStartupId");
    setIsMatrimonyLoggedIn(false);
  };

  const loginInvestor = (token, investor) => {
    localStorage.setItem("investorToken", token);
    localStorage.setItem("investor", JSON.stringify(investor));

    setIsInvestorLoggedIn(true);
    setInvestor(investor);

    scheduleLogout(token, logoutInvestor, investorTimer);

    if (localStorage.getItem("Startuptoken")) logoutStartup();
    if (localStorage.getItem("matrimonyToken")) logoutMatrimony();
  };

  const logoutInvestor = () => {
    clearTimeout(investorTimer.current);
    localStorage.removeItem("investorToken");
    localStorage.removeItem("investor");
    setIsInvestorLoggedIn(false);
    setInvestor(null);
  };

  const createSubscription = async planId => {
    try {
      await api.createSubscription(planId);
      setLoadingSubscription(true);
      const arr = await getUserSubscriptions();
      setSubscription(arr?.[0] || null);
      setSubscriptionError(false);
    } catch {
      setSubscriptionError(true);
    } finally {
      setLoadingSubscription(false);
    }
  };

  const logoutMentor = () => {
  clearTimeout(mentorTimer.current);
  localStorage.removeItem("mentorToken");
  localStorage.removeItem("mentor");
  setIsMentorLoggedIn(false);
  setMentor(null);
};


  const loginMentor = (token, mentor) => {
  localStorage.setItem("mentorToken", token);
  localStorage.setItem("mentor", JSON.stringify(mentor));

  setIsMentorLoggedIn(true);
  setMentor(mentor);

  scheduleLogout(token, logoutMentor, mentorTimer);

  
  if (localStorage.getItem("Startuptoken")) logoutStartup();
  if (localStorage.getItem("matrimonyToken")) logoutMatrimony();
  if (localStorage.getItem("investorToken")) logoutInvestor();
};

 const mentorHelper = useEffect(() => {
  try {
    return JSON.parse(localStorage.getItem("mentor")) || {};
  } catch {
    return {};
  }
}, []);

  


  /* --------------------------------------------------------- */
  return (
    <AuthContext.Provider
      value={{
        initialized,
        isStartupLoggedIn,
        isMatrimonyLoggedIn,
        isInvestorLoggedIn,
           isMentorLoggedIn,
            mentor,
        investor,
        subscription,
        loadingSubscription,
        subscriptionError,
        loginStartup,
        logoutStartup,
        loginMatrimony,
        logoutMatrimony,
        loginInvestor,
        logoutInvestor,
        loginMentor,
    logoutMentor,
        createSubscription,
        mentorHelper
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
